"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { clearEntireIndexedDB } from "@/hooks/useIndexedDBStorage";
import { userService } from "@/services";

// Default values for user object when data is missing
const DEFAULT_USER_VALUES = {
  id: "unknown",
  email: "user@example.com",
  name: "User",
  role: "user" as const,
};

interface ISession {
  isAuthenticated: boolean;
  user: any | null;
}

interface ISessionContext extends ISession {
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setAuthenticated: (isAuth: boolean, user?: any | null) => void;
  hasRole: (role: string) => boolean;
  saveRedirectUrl: (url: string) => void;
  getRedirectUrl: () => string;
}

export const SessionContext = createContext<ISessionContext | undefined>(
  undefined,
);

export const useSession = () => {
  const ctx = useContext(SessionContext);

  if (!ctx) {
    // Return a fallback context for SSR/build time
    return {
      isAuthenticated: false,
      user: null,
      logout: async () => {},
      refresh: async () => {},
      setAuthenticated: () => {},
      hasRole: () => false,
      saveRedirectUrl: () => {},
      getRedirectUrl: () => "/",
    };
  }
  return ctx;
};

export const SessionProvider = ({
  children,
  initialAuth,
  initialUser,
}: {
  children: ReactNode;
  initialAuth: boolean;
  initialUser: any | null;
}) => {
  const queryClient = useQueryClient();

  const fetchSession = async (): Promise<ISession> => {
    try {
      // Lightweight endpoint; falls back to user-info if needed
      const res = await fetch("/api/auth/status", { credentials: "include" });
      if (!res.ok) {
        return { isAuthenticated: false, user: null };
      }
      const data = await res.json();

      // Debug logging in development
      if (process.env.NODE_ENV === "development") {
        console.log("SessionProvider fetchSession response:", data);
      }

      // If authenticated but no userInfo, create minimal user object
      if (data.isAuthenticated && !data.userInfo) {
        // Try to extract from localStorage or create default
        const storedUser =
          typeof window !== "undefined" ? localStorage.getItem("user") : null;

        if (storedUser) {
          try {
            data.userInfo = JSON.parse(storedUser);
          } catch {
            // Create minimal user object for authenticated users
            data.userInfo = DEFAULT_USER_VALUES;
          }
        } else {
          // Create minimal user object for authenticated users
          data.userInfo = DEFAULT_USER_VALUES;
        }
      }

      return {
        isAuthenticated: data.isAuthenticated,
        user: data.userInfo ?? null,
      };
    } catch (error) {
      console.error("SessionProvider fetchSession error:", error);
      return { isAuthenticated: false, user: null };
    }
  };

  const hasInitialAuth = initialAuth || !!initialUser;

  const { data: session, refetch } = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
    // Provide initialData only when we actually have something trustworthy from server
    ...(hasInitialAuth && {
      initialData: { isAuthenticated: initialAuth, user: initialUser },
    }),
    staleTime: 60 * 1000,
    refetchOnMount: "always",
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (session?.isAuthenticated) {
      return;
    }

    const cookieStr = document.cookie;
    const hasAccessToken =
      cookieStr.includes("accessToken=") || cookieStr.includes("access_token=");

    if (hasAccessToken) {
      // Optimistically set auth true while fetching real user data
      queryClient.setQueryData(["session"], {
        isAuthenticated: true,
        user: session?.user ?? null,
      });
      // Refresh in background to sync user data
      void refetch();
    }
  }, [session?.isAuthenticated, queryClient, refetch, session?.user]);

  /**
   * Reactivation ping: when the window/tab gains focus after being inactive
   * for a while, hit the lightweight `/api/ping` endpoint. That endpoint will
   * attempt a silent token refresh if the `access_token` is missing/expired
   * but a valid `refresh_token` exists. Doing this _before_ the user performs
   * the first authenticated action prevents 401 "No access token found".
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleFocus = async () => {
      try {
        await fetch("/api/ping", { credentials: "include" });
        // After ping we proactively refresh session info
        void refetch();
      } catch {
        /* no-op */
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetch]);

  const logout = useCallback(async () => {
    await userService.logout();
    await clearEntireIndexedDB();
    queryClient.setQueryData(["session"], {
      isAuthenticated: false,
      user: null,
    });
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const setAuthenticated = useCallback(
    (auth: boolean, userData?: any | null) => {
      queryClient.setQueryData(["session"], {
        isAuthenticated: auth,
        user: userData ?? null,
      });
    },
    [queryClient],
  );

  const hasRole = useCallback(
    (role: string) => {
      // Single role system with hierarchy checking
      const user = session?.user;
      if (!user) return false;

      // Role hierarchy for access control
      const ROLE_HIERARCHY: Record<string, string[]> = {
        super_admin: [
          "admin",
          "realtor",
          "main_realtor",
          "secretary",
          "user",
          "owner",
          "guest",
          "user_realtor",
        ],
        admin: [
          "realtor",
          "main_realtor",
          "secretary",
          "user",
          "owner",
          "guest",
          "user_realtor",
        ],
        main_realtor: ["realtor", "user", "owner", "guest", "user_realtor"],
        realtor: ["user", "owner", "guest", "user_realtor"],
        secretary: ["user", "owner", "guest"],
        user_realtor: ["user", "owner", "guest"],
        owner: ["user", "guest"],
        user: ["guest"],
        guest: [],
      };

      // Check single role field
      if (user.role) {
        // Direct role match
        if (user.role === role) return true;

        // Check role hierarchy - higher roles can access lower role functions
        const allowedRoles = ROLE_HIERARCHY[user.role] || [];
        return allowedRoles.includes(role);
      }

      return false;
    },
    [session?.user],
  );

  const saveRedirectUrl = (url: string) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("authRedirectUrl", url);
    }
  };

  const getRedirectUrl = () => {
    if (typeof window !== "undefined") {
      const url = sessionStorage.getItem("authRedirectUrl");
      if (url) {
        sessionStorage.removeItem("authRedirectUrl");
        return url;
      }
    }
    return "/";
  };

  const safeSession = session || {
    isAuthenticated: initialAuth,
    user: initialUser,
  };

  return (
    <SessionContext.Provider
      value={{
        isAuthenticated: safeSession.isAuthenticated,
        user: safeSession.user,
        logout,
        refresh,
        setAuthenticated,
        hasRole,
        saveRedirectUrl,
        getRedirectUrl,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
